import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SEO from "@/components/SEO";
import LandingHeader from "@/components/landing/LandingHeader";
import LandingHero from "@/components/landing/LandingHero";
import LandingTrustStrip from "@/components/landing/LandingTrustStrip";
import LandingRecentWork from "@/components/landing/LandingRecentWork";
import LandingProcess from "@/components/landing/LandingProcess";
import LandingQuestions from "@/components/landing/LandingQuestions";
import LandingFinalCta from "@/components/landing/LandingFinalCta";
import LandingFooter from "@/components/landing/LandingFooter";
import LandingStickyBar from "@/components/landing/LandingStickyBar";
import {
  LandingContentProvider,
  useLandingContent,
} from "@/lib/landing/LandingContentProvider";
import { pickVariant, webDevelopmentSanDiego } from "@/lib/landing/registry";
import { phoneSchemaForm } from "@/lib/landing/resolve";
import { trackEvent, trackInternalEvent } from "@/lib/track";

const canonicalPath = "/web-development-san-diego";
export default function WebDevLanding() {
  return (
    <LandingContentProvider definition={webDevelopmentSanDiego}>
      <WebDevLandingPage />
    </LandingContentProvider>
  );
}

function WebDevLandingPage() {
  const { content, definition } = useLandingContent();
  const [params] = useSearchParams();
  const variant = pickVariant(content, params.get("v")).key;
  const telephone = phoneSchemaForm(content.phone);
  const [step, setStep] = useState<1 | 2>(1);
  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Web Development",
        serviceType: "Web development",
        areaServed: "San Diego County, CA",
        provider: {
          "@type": "LocalBusiness",
          name: "Smartly Technologies",
          address: {
            "@type": "PostalAddress",
            streetAddress: "333 H St Suite 5000",
            addressLocality: "Chula Vista",
            addressRegion: "CA",
            postalCode: "91910",
          },
          telephone,
          url: "https://smartly.net",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://smartly.net/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Web Development in San Diego",
            item: `https://smartly.net${canonicalPath}`,
          },
        ],
      },
    ],
    [telephone],
  );
  const scrollToBook = () => {
    if (step === 1)
      document
        .querySelector<HTMLInputElement>('#book input[name="name"]')
        ?.focus({ preventScroll: true });
    document
      .getElementById("book")
      ?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
        block: "start",
      });
  };
  const call = (link_location: string) => {
    trackEvent("call_click", {
      lead_source: definition.leadSource,
      page_variant: variant,
      link_location,
    });
    trackInternalEvent("call_click", link_location);
  };
  return (
    <div className="web-dev-landing">
      <SEO
        title={content.seo.title}
        description={content.seo.description}
        canonicalPath={canonicalPath}
        jsonLd={jsonLd}
        noindex
      />
      <LandingHeader onCall={() => call("header")} />
      <main>
        <LandingHero
          variant={variant}
          step={step}
          onStepChange={setStep}
          onCall={() => call("hero")}
          onBookingHelpCall={() => call("booking_help")}
        />
        <LandingTrustStrip />
        <LandingRecentWork />
        <LandingProcess onBook={scrollToBook} onCall={() => call("steps")} />
        <LandingQuestions />
        <LandingFinalCta
          onBook={scrollToBook}
          onCall={() => call("final_cta")}
        />
      </main>
      <LandingFooter onCall={() => call("footer")} />
      <LandingStickyBar
        step={step}
        onBook={scrollToBook}
        onCall={() => call("sticky_bar")}
      />
    </div>
  );
}
